import React from 'react'
import { useParams } from 'react-router'
import { ErroredInstance, isErrored, isLoading, LoadingInstance } from 'rwwa-data-access'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { AdditionalDetails } from './Components/AdditionalDetails'
import { AddressAutocomplete } from './AddressAutocomplete'
import { useSplitAddress } from '@mobi/account/Common/Address/Hooks/useSplitAddress'
import { ButtonBlock } from '@core/Components/Buttons/ButtonBlock'
import { CheckboxInput } from '@mobi/component-library/Legacy/Input/CheckboxInput'
import { ContactDetailsReverificationSuccess } from '../Reverification/Components/ReverificationComplete'
import { CustomerServiceTelephone } from '@mobi/component-library/Base/Link'
import { DesktopSpinner, Spinner } from '@mobi/component-library/Legacy/Spinner'
import { connect } from '@core/Components/HOCs/connect'
import { ErrorMessage } from '../../Components/Messages'
import { FailedSaveMessage } from './Components/FailedSaveMessage/FailedSaveMessage'
import { getContactDetailsFields } from './data-transforms'
import { Grid, GridCell } from '@mobi/component-library/Legacy/Grid'
import { MainContainer } from '../../Components/Containers'
import { ManualAddressEntry } from './Components/ManualAddressEntry'
import { NameDateOfBirthChangedNotice } from './Components/NameDateOfBirthChangedNotice/NameDateOfBirthChangedNotice'
import { appHistory } from '@core/Utils/routing/history'
import { observeImmutable } from '../../Components/HOCs/observe'
import { ChangeResidentialAddress, Reset } from '@core/Areas/ContactDetails/driver'
import { useBetAccount } from '@mobi/account/Hooks'
import { useLogon } from '@core/Utils/hooks'
import {
  ContactDetails as ContactDetailsRepo,
  ContactDetailsKey,
} from '../../Data/Account/contactDetails'
import type { BetAccountHolder, AccountHolderAddress } from '@mobi/api-types'
import {
  ShowManualAddressForm,
  ShowManualPostalAddressForm,
} from './Components/ManualAddressEntry/driver'
import {
  state$,
  type ContactDetailsFields,
  type ContactDetailsStateRecord,
  ChangeValue,
  ChangePostalAddress,
  UpdateFields,
  SaveButtonClicked,
  type ContactDetailsState,
  isFieldValidationSuccess,
} from './driver'
import {
  ContactDetailsContainer,
  ContactDetailsPreferredNameStyled,
  ContactDetailsItemStyled,
  ContactDetailsTitleLabelStyled,
  ContactDetailsEditLabelStyled,
  ContactDetailsManualAddressButton,
} from './ContactDetails.styles'

export interface ContactEditDetailsProps {
  isDesktop?: boolean
  betAccountHolderNumber?: string | null
  navigateOnUpdateSuccess: () => void
}

export const ContactDetailsEditMobiWrapper = () => {
  const { accountNumber } = useParams<{ accountNumber?: string }>()

  return (
    <ContactDetailsEdit
      isDesktop={false}
      betAccountHolderNumber={accountNumber}
      navigateOnUpdateSuccess={() => appHistory.push('/account/contactdetails')}
    />
  )
}

export const ContactDetailsEdit = ({
  isDesktop,
  betAccountHolderNumber,
  navigateOnUpdateSuccess,
}: ContactEditDetailsProps) => {
  return (
    <ConnectContactDetails
      contactDetails={betAccountHolderNumber || ContactDetailsKey}
      isDesktop={isDesktop}
      navigateOnUpdateSuccess={navigateOnUpdateSuccess}
    />
  )
}

export const ContactDetailsEditLoader = ({
  contactDetails,
  isDesktop,
  navigateOnUpdateSuccess,
}: {
  contactDetails: typeof LoadingInstance | typeof ErroredInstance | BetAccountHolder
} & ContactEditDetailsProps) => {
  const { accountNumber, isLoggedIn } = useLogon()
  const betAccountQuery = useBetAccount({
    enabled: accountNumber !== null && isLoggedIn === true,
  })

  React.useEffect(() => Reset(), [])

  if (isLoading(contactDetails) || betAccountQuery.isLoading) {
    return isDesktop ? <DesktopSpinner /> : <Spinner />
  }

  if (isErrored(contactDetails) || betAccountQuery.isError) {
    return <ErrorMessage>An error occurred while loading your contact details.</ErrorMessage>
  }

  // Catch non-verified users
  if (betAccountQuery.data?.IsIDVerified === false) {
    return null
  }

  const props = getContactDetailsFields(contactDetails)
  return (
    <ContactDetailsEditSetup
      {...props}
      isDesktop={isDesktop}
      navigateOnUpdateSuccess={navigateOnUpdateSuccess}
    />
  )
}

const ConnectContactDetails = connect({
  contactDetails: ContactDetailsRepo,
})<ContactEditDetailsProps>(ContactDetailsEditLoader)

type ContactDetailsEditSetupProps = ContactDetailsFields &
  Pick<ContactEditDetailsProps, 'isDesktop' | 'navigateOnUpdateSuccess'>

class ContactDetailsEditSetup extends React.Component<ContactDetailsEditSetupProps> {
  public componentDidMount() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isDesktop, navigateOnUpdateSuccess, ...fields } = this.props
    UpdateFields(fields)
  }

  public render() {
    const { isDesktop, navigateOnUpdateSuccess } = this.props
    return (
      <ContactDetailsEditObserver
        isDesktop={isDesktop}
        navigateOnUpdateSuccess={navigateOnUpdateSuccess}
      />
    )
  }
}

const ContactDetailsEditObserver = observeImmutable<
  ContactDetailsStateRecord,
  Pick<ContactEditDetailsProps, 'isDesktop'> &
    Pick<ContactEditDetailsProps, 'navigateOnUpdateSuccess'>
>(state$, ({ record, isDesktop, navigateOnUpdateSuccess }) => {
  const state: ContactDetailsState = record.toJS()

  if (state.updateSuccess) {
    navigateOnUpdateSuccess()
    return null
  }

  return <ContactDetailsEditComponent {...state} isDesktop={isDesktop} />
})

const changeAddressPostalSame = (event: React.FormEvent<HTMLInputElement>) =>
  ChangeValue({ field: 'addressPostalSame', value: event.currentTarget.checked })
const changeNameDateOfBirthChanged = (event: React.FormEvent<HTMLInputElement>) =>
  ChangeValue({ field: 'nameDateOfBirthChanged', value: event.currentTarget.checked })

export const ContactDetailsEditComponent = ({
  fullName,
  preferredName,
  addressResidentialMoniker,
  addressPostalMoniker,
  addressPostalSame,
  validationErrors,
  updateFailed,
  loading,
  isAdditionalDetailsChanged,
  agreeToVerifyAdditionalDetails,
  isDesktop,
  nameDateOfBirthChanged,
  nameDateOfBirthChangedInitial,
  isInternationalCustomer,
}: ContactDetailsState & Pick<ContactEditDetailsProps, 'isDesktop'>) => {
  const fieldValidationSucceeded = isFieldValidationSuccess(
    addressPostalSame,
    isAdditionalDetailsChanged,
    !!agreeToVerifyAdditionalDetails,
    validationErrors
  )

  const [residentialStreetNumberError, setResidentialStreetNumberError] = React.useState('')
  const [postalStreetNumberError, setPostalStreetNumberError] = React.useState('')
  const [isEmptyResidentialAddress, setIsEmptyResidentialAddress] = React.useState(false)
  const [isEmptyPostalAddress, setIsEmptyPostalAddress] = React.useState(false)

  React.useEffect(() => {
    if (addressPostalSame) {
      setPostalStreetNumberError('')
      setIsEmptyPostalAddress(false)
    }
  }, [addressPostalSame])

  const splitResidentialAddress = useSplitAddress({
    onSuccess: (addressDetails: AccountHolderAddress, moniker: string) => {
      setResidentialStreetNumberError(getStreetNumberError(addressDetails))
      if (addressDetails.streetNumber.length >= 1) {
        ChangeResidentialAddress({ addressResidentialMoniker: moniker })
      }
    },
    onError: () => {
      setResidentialStreetNumberError(
        'Unable to confirm address. Please try again or enter manually.'
      )
    },
  })

  const splitPostalAddress = useSplitAddress({
    onSuccess: (addressDetails: AccountHolderAddress, moniker: string) => {
      setPostalStreetNumberError(getStreetNumberError(addressDetails))
      if (addressDetails.streetNumber.length >= 1) {
        ChangePostalAddress({ addressPostalMoniker: moniker })
      }
    },
    onError: () => {
      setPostalStreetNumberError('Unable to confirm address. Please try again or enter manually.')
    },
  })

  const handleCancel = () => {
    if (isDesktop) {
      window.history.back()
    } else {
      appHistory.back()
    }
  }

  return (
    <MainContainer>
      <ContactDetailsReverificationSuccess isDesktop={!!isDesktop} navigateToHistoryBack={true} />

      {!isDesktop && <PageHeader title='Edit Contact Details' />}

      <ContactDetailsContainer>
        {preferredName && (
          <ContactDetailsPreferredNameStyled>{preferredName}</ContactDetailsPreferredNameStyled>
        )}
        <ContactDetailsItemStyled>{fullName}</ContactDetailsItemStyled>

        <form>
          <fieldset>
            {isInternationalCustomer ? (
              <ReadonlyAddress
                addressResidentialMoniker={addressResidentialMoniker}
                addressPostalMoniker={addressPostalMoniker}
                addressPostalSame={addressPostalSame}
              />
            ) : (
              <EditableAddress
                addressResidentialMoniker={addressResidentialMoniker}
                addressPostalMoniker={addressPostalMoniker}
                addressPostalSame={addressPostalSame}
                validationErrors={validationErrors}
                residentialStreetNumberError={residentialStreetNumberError}
                postalStreetNumberError={postalStreetNumberError}
                handleResidentialAddressMoniker={(value: string | null) => {
                  setIsEmptyResidentialAddress(!value)
                  if (value) {
                    splitResidentialAddress.mutate(value)
                  }
                }}
                handlePostalAddressMoniker={(value: string | null) => {
                  setIsEmptyPostalAddress(!value)
                  if (value) {
                    splitPostalAddress.mutate(value)
                  }
                }}
              />
            )}

            {isInternationalCustomer && (
              <CheckboxInput
                id='contact-centre-checkbox'
                data-tid-contact-centre-checkbox=''
                checked={nameDateOfBirthChanged}
                disabled={nameDateOfBirthChangedInitial}
                onChange={changeNameDateOfBirthChanged}
                label={
                  <>
                    For international address changes please select the tick box and a customer
                    representative will contact you. Alternatively call{' '}
                    <CustomerServiceTelephone isInternational={isInternationalCustomer} />.
                  </>
                }
              />
            )}
          </fieldset>
        </form>

        <ManualAddressEntry />

        <NameDateOfBirthChangedNotice />

        <AdditionalDetails />

        <Grid padding='0.25rem'>
          <GridCell data-tid-cancel-button=''>
            <ButtonBlock color='secondary' onClick={handleCancel}>
              Cancel
            </ButtonBlock>
          </GridCell>

          <GridCell data-tid-save-button=''>
            <ButtonBlock
              onClick={SaveButtonClicked}
              disabled={
                !fieldValidationSucceeded ||
                loading ||
                !!residentialStreetNumberError ||
                !!postalStreetNumberError ||
                isEmptyResidentialAddress ||
                isEmptyPostalAddress
              }
            >
              Save
            </ButtonBlock>

            {loading ? <Spinner /> : null}
          </GridCell>
        </Grid>
        <FailedSaveMessage showFailedMessage={updateFailed} />
      </ContactDetailsContainer>
    </MainContainer>
  )
}

const ReadonlyAddress = ({
  addressPostalSame,
  addressResidentialMoniker,
  addressPostalMoniker,
}: Pick<
  ContactDetailsState,
  'addressPostalSame' | 'addressResidentialMoniker' | 'addressPostalMoniker'
>) => {
  return (
    <div>
      <ContactDetailsTitleLabelStyled>Residential Address</ContactDetailsTitleLabelStyled>
      <ContactDetailsItemStyled>{addressResidentialMoniker}</ContactDetailsItemStyled>

      {!addressPostalSame && (
        <ContactDetailsTitleLabelStyled>Postal Address</ContactDetailsTitleLabelStyled>
      )}
      {!addressPostalSame && (
        <ContactDetailsItemStyled>{addressPostalMoniker}</ContactDetailsItemStyled>
      )}
    </div>
  )
}

const EditableAddress = ({
  addressPostalSame,
  addressResidentialMoniker,
  addressPostalMoniker,
  validationErrors,
  residentialStreetNumberError,
  postalStreetNumberError,
  handleResidentialAddressMoniker,
  handlePostalAddressMoniker,
}: Pick<
  ContactDetailsState,
  'addressPostalSame' | 'addressResidentialMoniker' | 'addressPostalMoniker' | 'validationErrors'
> & {
  residentialStreetNumberError: string
  postalStreetNumberError: string
  handleResidentialAddressMoniker: (value: string | null) => void
  handlePostalAddressMoniker: (value: string | null) => void
}) => {
  return (
    <div>
      <ContactDetailsManualAddressButton type='button' onClick={ShowManualAddressForm}>
        Manually Enter Address
      </ContactDetailsManualAddressButton>
      <ContactDetailsEditLabelStyled htmlFor='contact-address'>
        Residential Address<span>*</span>
      </ContactDetailsEditLabelStyled>

      <AddressAutocomplete
        id='contact-address-autocomplete'
        inputId='contact-address'
        value={addressResidentialMoniker}
        onSelection={handleResidentialAddressMoniker}
        errorMessage={validationErrors.addressResidentialMoniker || residentialStreetNumberError}
      />

      <CheckboxInput
        id='postal-address-checkbox'
        data-tid-postal-address-checkbox=''
        checked={addressPostalSame}
        onChange={changeAddressPostalSame}
        label='This is my postal address.'
      />

      {addressPostalSame ? null : (
        <ContactDetailsManualAddressButton type='button' onClick={ShowManualPostalAddressForm}>
          Manually Enter Address
        </ContactDetailsManualAddressButton>
      )}
      {addressPostalSame ? null : (
        <ContactDetailsEditLabelStyled htmlFor='contact-address-postal'>
          Postal address<span>*</span>
        </ContactDetailsEditLabelStyled>
      )}
      {addressPostalSame ? null : (
        <AddressAutocomplete
          id='contact-address-postal-autocomplete'
          inputId='contact-address-postal'
          value={addressPostalMoniker}
          onSelection={handlePostalAddressMoniker}
          autoFocus={!!validationErrors.addressPostalMoniker}
          errorMessage={validationErrors.addressPostalMoniker || postalStreetNumberError}
        />
      )}
    </div>
  )
}

export const getStreetNumberError = (addressDetails: AccountHolderAddress) =>
  addressDetails.streetNumber.length < 1
    ? 'We require a street number. Please try another address or enter your address manually.'
    : ''
